import { useState, useEffect } from "react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { AdminNavigation } from "@/components/AdminNavigation";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, FileText, Shield } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { DocumentWatermark } from "@/components/DocumentWatermark";
import { getDocumentVersion, initializeDocument } from "@/lib/documentVersion";

const AdminVerarbeitungsverzeichnis = () => {
  const { toast } = useToast();
  const [docVersion, setDocVersion] = useState(getDocumentVersion('verarbeitungsverzeichnis'));

  useEffect(() => {
    const version = initializeDocument('verarbeitungsverzeichnis', '1.0');
    setDocVersion(version);
  }, []);
  const verantwortlicher = {
    name: "EAP® Produktions- und Patentverwertungs-GmbH",
    geschaeftsfuehrer: "Ing. Dr. med. dent. Mario Kern M.Sc. M.Sc.",
    adresse: "Lorettostraße 26, 2. und 3. Stock",
    plz_ort: "6060 Hall in Tirol",
    land: "Austria",
    firmenbuch: "FB-Gericht Innsbruck, FN 483289w",
    uid: "ATU72852709",
    website: "eap-abutments.com"
  };

  const verarbeitungen = [
    {
      id: "vv-001",
      name: "Kontaktformular Website",
      kategorie: "Kundenanfragen",
      zweck: "Beantwortung von Kundenanfragen und vorvertragliche Kommunikation",
      betroffenePersonen: [
        "Interessenten",
        "Zahnärzte",
        "Zahntechniker",
        "Dentallabore"
      ],
      datenKategorien: [
        "Name",
        "E-Mail-Adresse",
        "Telefonnummer (optional)",
        "Nachrichteninhalt",
        "Zeitstempel der Anfrage"
      ],
      empfaenger: [
        "Interne Vertriebsmitarbeiter",
        "Geschäftsführung"
      ],
      drittlaender: "Nein",
      rechtsgrundlage: "Art. 6 Abs. 1 lit. b DSGVO (vorvertragliche Maßnahmen) bzw. Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse)",
      loeschfrist: "3 Jahre nach letztem Kontakt bzw. Ablehnung der Geschäftsbeziehung",
      speicherort: "Supabase (EU-Region Frankfurt)",
      toms: [
        "SSL/TLS-Verschlüsselung bei Übertragung",
        "Zugriffsbeschränkung nur für autorisierte Mitarbeiter",
        "Verschlüsselte Datenbankablage",
        "Regelmäßige Backups",
        "Auftragsverarbeitungsvertrag mit Hosting-Provider"
      ]
    },
    {
      id: "vv-002",
      name: "Video-Terminbuchung",
      kategorie: "Terminmanagement",
      zweck: "Verwaltung von Beratungsterminen via Video-Call",
      betroffenePersonen: [
        "Zahnärzte",
        "Zahntechniker",
        "Interessenten"
      ],
      datenKategorien: [
        "Name",
        "E-Mail-Adresse",
        "Telefonnummer",
        "Wunschtermin",
        "Nachricht/Anliegen",
        "Terminbestätigungen",
        "Zeitstempel"
      ],
      empfaenger: [
        "Interne Mitarbeiter (Kalenderverwaltung)",
        "Geschäftsführung",
        "E-Mail-Service-Provider (World4You SMTP)"
      ],
      drittlaender: "Nein (EU-Server in Österreich)",
      rechtsgrundlage: "Art. 6 Abs. 1 lit. b DSGVO (Vertragsanbahnung)",
      loeschfrist: "1 Jahr nach Termin bzw. nach Stornierung",
      speicherort: "Supabase (EU-Region Frankfurt)",
      toms: [
        "Verschlüsselte Speicherung",
        "SSL-Verschlüsselung",
        "Zugriffskontrolle",
        "Automatische Löschung nach Fristablauf",
        "E-Mail-Versand über zertifizierten EU-Provider (World4You, Österreich)"
      ]
    },
    {
      id: "vv-003",
      name: "Google Analytics 4",
      kategorie: "Website-Analyse",
      zweck: "Analyse des Nutzerverhaltens zur Optimierung der Website",
      betroffenePersonen: [
        "Alle Website-Besucher"
      ],
      datenKategorien: [
        "IP-Adresse (anonymisiert)",
        "Browser-Informationen",
        "Geräteinformationen",
        "Seitenaufrufe",
        "Verweildauer",
        "Herkunft (Referrer)",
        "Demographische Daten (geschätzt)",
        "Interessen (geschätzt)"
      ],
      empfaenger: [
        "Google Ireland Limited",
        "Google LLC (USA)"
      ],
      drittlaender: "Ja - USA (Standardvertragsklauseln gemäß Art. 46 DSGVO)",
      rechtsgrundlage: "Art. 6 Abs. 1 lit. a DSGVO (Einwilligung via Cookie-Banner)",
      loeschfrist: "14 Monate (in Google Analytics konfiguriert)",
      speicherort: "Google Cloud (EU und USA)",
      toms: [
        "IP-Anonymisierung aktiviert",
        "Auftragsverarbeitungsvertrag mit Google",
        "Opt-out-Möglichkeit via Cookie-Banner",
        "Keine Verknüpfung mit anderen Google-Diensten",
        "Datenweitergabe an Google deaktiviert"
      ]
    },
    {
      id: "vv-004",
      name: "Facebook Pixel",
      kategorie: "Marketing & Retargeting",
      zweck: "Messung der Werbewirksamkeit und Retargeting",
      betroffenePersonen: [
        "Website-Besucher mit aktivierten Marketing-Cookies"
      ],
      datenKategorien: [
        "IP-Adresse",
        "Browser-Informationen",
        "Besuchte Seiten",
        "Interaktionen (Button-Klicks)",
        "Facebook-Cookie-ID"
      ],
      empfaenger: [
        "Meta Platforms Ireland Limited",
        "Meta Platforms Inc. (USA)"
      ],
      drittlaender: "Ja - USA (Standardvertragsklauseln gemäß Art. 46 DSGVO)",
      rechtsgrundlage: "Art. 6 Abs. 1 lit. a DSGVO (Einwilligung via Cookie-Banner)",
      loeschfrist: "180 Tage (Facebook-Standard)",
      speicherort: "Meta-Server (EU und USA)",
      toms: [
        "Auftragsverarbeitungsvertrag mit Meta",
        "Opt-in via Cookie-Banner erforderlich",
        "Transparenz in Datenschutzerklärung",
        "Standard Contract Clauses (SCC) für USA-Transfer"
      ]
    },
    {
      id: "vv-005",
      name: "Google Fonts",
      kategorie: "Website-Darstellung",
      zweck: "Optimale Darstellung der Website-Schriftarten",
      betroffenePersonen: [
        "Alle Website-Besucher"
      ],
      datenKategorien: [
        "IP-Adresse",
        "Browser-Informationen",
        "Zeitpunkt des Zugriffs"
      ],
      empfaenger: [
        "Google Ireland Limited",
        "Google LLC (USA)"
      ],
      drittlaender: "Ja - USA",
      rechtsgrundlage: "Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an einheitlicher Darstellung)",
      loeschfrist: "Unmittelbar (keine Speicherung durch EAP)",
      speicherort: "Google-Server (CDN)",
      toms: [
        "Keine dauerhafte Speicherung von IP-Adressen durch EAP",
        "Verweis in Datenschutzerklärung",
        "Möglichkeit: Lokales Hosting als Alternative"
      ]
    },
    {
      id: "vv-006",
      name: "Cookie-Verwaltung",
      kategorie: "Cookie-Consent",
      zweck: "Verwaltung von Cookie-Einwilligungen",
      betroffenePersonen: [
        "Alle Website-Besucher"
      ],
      datenKategorien: [
        "Cookie-Präferenzen",
        "Zeitstempel der Einwilligung",
        "Browser-ID (lokal)"
      ],
      empfaenger: [
        "Keine (nur lokale Speicherung im Browser)"
      ],
      drittlaender: "Nein",
      rechtsgrundlage: "Art. 6 Abs. 1 lit. c DSGVO (rechtliche Verpflichtung gemäß ePrivacy)",
      loeschfrist: "12 Monate oder bei Widerruf",
      speicherort: "Browser des Nutzers (localStorage)",
      toms: [
        "Nur im Browser gespeichert",
        "Keine Übertragung an Server",
        "Opt-in für nicht-notwendige Cookies",
        "Einfache Widerrufsmöglichkeit"
      ]
    },
    {
      id: "vv-008",
      name: "Admin-Panel & Nutzerauthentifizierung",
      kategorie: "Systemverwaltung",
      zweck: "Verwaltung des Admin-Bereichs und sichere Authentifizierung",
      betroffenePersonen: [
        "Administratoren",
        "Autorisierte Mitarbeiter"
      ],
      datenKategorien: [
        "E-Mail-Adresse",
        "Passwort (gehasht)",
        "Session-Tokens",
        "Login-Zeitstempel",
        "IP-Adresse bei Login"
      ],
      empfaenger: [
        "Keine externe Weitergabe"
      ],
      drittlaender: "Nein",
      rechtsgrundlage: "Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an sicherer Systemverwaltung)",
      loeschfrist: "1 Jahr nach letztem Login",
      speicherort: "Supabase Auth (EU-Region Frankfurt)",
      toms: [
        "Passwort-Hashing (bcrypt)",
        "Session-Management",
        "Zwei-Faktor-Authentifizierung (optional)",
        "Zugriffsprotokolle",
        "Automatische Session-Timeout"
      ]
    }
  ];

  const downloadPDF = () => {
    window.print();
    toast({
      title: "Druckansicht geöffnet",
      description: "Speichern Sie das Dokument als PDF über Ihren Browser.",
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <AdminNavigation />
      <main className="max-w-full overflow-x-hidden pt-24 pb-24">
        <div className="container mx-auto px-4 max-w-7xl">
          <Breadcrumbs items={[
            { label: "Admin" },
            { label: "Dokumente", href: "/admin/documents" },
            { label: "Verarbeitungsverzeichnis" }
          ]} />
          
          
          <DocumentWatermark 
            documentName="Verarbeitungsverzeichnis nach Art. 30 DSGVO"
            version={docVersion}
          />

          <div className="mb-8 flex flex-col items-start justify-between gap-4 mt-6 sm:flex-row">
            <div>
              <h1 className="break-words text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-2">
                Verarbeitungsverzeichnis
              </h1>
              <p className="text-xl text-muted-foreground">
                nach Art. 30 DSGVO
              </p>
            </div>
            <Button onClick={downloadPDF} className="flex items-center gap-2">
              <Download className="w-4 h-4" />
              Als PDF speichern
            </Button>
          </div>

          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="flex h-auto w-full justify-start overflow-x-auto mb-8 p-1 [scrollbar-width:thin]">
              <TabsTrigger value="overview" className="shrink-0">Übersicht</TabsTrigger>
              <TabsTrigger value="details" className="shrink-0">Detaillierte Verarbeitungen</TabsTrigger>
            </TabsList>

            <TabsContent value="overview">
              <div className="space-y-6">
                {/* Verantwortlicher */}
                <Card className="p-4 sm:p-6">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="shrink-0 p-3 bg-primary/10 rounded-lg">
                      <Shield className="w-6 h-6 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <h2 className="text-2xl font-semibold text-foreground mb-2">
                        Verantwortlicher
                      </h2>
                      <p className="text-sm text-muted-foreground">
                        Gemäß Art. 4 Nr. 7 DSGVO
                      </p>
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4 bg-secondary/30 p-4 rounded-lg">
                    <div>
                      <p className="text-sm text-muted-foreground">Firmenname</p>
                      <p className="font-semibold text-foreground">{verantwortlicher.name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Geschäftsführer</p>
                      <p className="font-semibold text-foreground">{verantwortlicher.geschaeftsfuehrer}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Adresse</p>
                      <p className="font-semibold text-foreground">
                        {verantwortlicher.adresse}<br />
                        {verantwortlicher.plz_ort}, {verantwortlicher.land}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Registrierung</p>
                      <p className="font-semibold text-foreground">{verantwortlicher.firmenbuch}</p>
                      <p className="font-semibold text-foreground">{verantwortlicher.uid}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Website</p>
                      <p className="font-semibold text-foreground">{verantwortlicher.website}</p>
                    </div>
                  </div>
                </Card>

                {/* Verarbeitungsübersicht */}
                <Card className="p-4 sm:p-6">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="shrink-0 p-3 bg-primary/10 rounded-lg">
                      <FileText className="w-6 h-6 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <h2 className="text-2xl font-semibold text-foreground mb-2">
                        Verarbeitungsübersicht
                      </h2>
                      <p className="text-sm text-muted-foreground">
                        {verarbeitungen.length} dokumentierte Verarbeitungstätigkeiten
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {verarbeitungen.map((v, index) => (
                      <div key={v.id} className="bg-secondary/30 p-4 rounded-lg">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <span className="text-xs font-mono text-muted-foreground">{v.id}</span>
                            <h3 className="text-lg font-semibold text-foreground">{v.name}</h3>
                          </div>
                          <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary">
                            {v.kategorie}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground mb-2">{v.zweck}</p>
                        <div className="flex flex-wrap gap-2 text-xs">
                          <span className="px-2 py-1 bg-background rounded">
                            {v.betroffenePersonen.length} Personengruppen
                          </span>
                          <span className="px-2 py-1 bg-background rounded">
                            {v.datenKategorien.length} Datenkategorien
                          </span>
                          <span className={`px-2 py-1 rounded ${
                            v.drittlaender === "Ja" || v.drittlaender.startsWith("Ja") 
                              ? "bg-yellow-500/10 text-yellow-700" 
                              : "bg-green-500/10 text-green-700"
                          }`}>
                            {v.drittlaender === "Nein" ? "Nur EU" : "Drittland"}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="details">
              <div className="space-y-8">
                {verarbeitungen.map((v, index) => (
                  <Card key={v.id} className="p-6">
                    <div className="border-b border-border pb-4 mb-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <span className="text-sm font-mono text-muted-foreground">{v.id}</span>
                          <h2 className="text-2xl font-bold text-foreground">{v.name}</h2>
                        </div>
                        <span className="px-3 py-1 rounded-full bg-primary/10 text-primary font-medium">
                          {v.kategorie}
                        </span>
                      </div>
                    </div>

                    <div className="space-y-6">
                      {/* Zweck */}
                      <div>
                        <h3 className="text-sm font-semibold text-muted-foreground mb-2">
                          ZWECK DER VERARBEITUNG
                        </h3>
                        <p className="text-foreground">{v.zweck}</p>
                      </div>

                      {/* Betroffene Personen */}
                      <div>
                        <h3 className="text-sm font-semibold text-muted-foreground mb-2">
                          KATEGORIEN BETROFFENER PERSONEN
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {v.betroffenePersonen.map((person, i) => (
                            <span key={i} className="px-3 py-1 bg-secondary text-foreground rounded-md">
                              {person}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Datenkategorien */}
                      <div>
                        <h3 className="text-sm font-semibold text-muted-foreground mb-2">
                          KATEGORIEN PERSONENBEZOGENER DATEN
                        </h3>
                        <ul className="list-disc list-inside space-y-1">
                          {v.datenKategorien.map((kat, i) => (
                            <li key={i} className="text-foreground">{kat}</li>
                          ))}
                        </ul>
                      </div>

                      {/* Empfänger */}
                      <div>
                        <h3 className="text-sm font-semibold text-muted-foreground mb-2">
                          KATEGORIEN VON EMPFÄNGERN
                        </h3>
                        <ul className="list-disc list-inside space-y-1">
                          {v.empfaenger.map((emp, i) => (
                            <li key={i} className="text-foreground">{emp}</li>
                          ))}
                        </ul>
                      </div>

                      {/* Drittländer */}
                      <div>
                        <h3 className="text-sm font-semibold text-muted-foreground mb-2">
                          ÜBERMITTLUNG IN DRITTLÄNDER
                        </h3>
                        <p className={`font-medium ${
                          v.drittlaender === "Nein" ? "text-green-600" : "text-yellow-600"
                        }`}>
                          {v.drittlaender}
                        </p>
                      </div>

                      {/* Rechtsgrundlage */}
                      <div>
                        <h3 className="text-sm font-semibold text-muted-foreground mb-2">
                          RECHTSGRUNDLAGE
                        </h3>
                        <p className="text-foreground font-medium">{v.rechtsgrundlage}</p>
                      </div>

                      {/* Löschfrist */}
                      <div>
                        <h3 className="text-sm font-semibold text-muted-foreground mb-2">
                          LÖSCHFRISTEN
                        </h3>
                        <p className="text-foreground">{v.loeschfrist}</p>
                      </div>

                      {/* Speicherort */}
                      <div>
                        <h3 className="text-sm font-semibold text-muted-foreground mb-2">
                          SPEICHERORT
                        </h3>
                        <p className="text-foreground">{v.speicherort}</p>
                      </div>

                      {/* TOMs */}
                      <div>
                        <h3 className="text-sm font-semibold text-muted-foreground mb-2">
                          TECHNISCHE UND ORGANISATORISCHE MASSNAHMEN (TOMs)
                        </h3>
                        <ul className="list-disc list-inside space-y-1">
                          {v.toms.map((tom, i) => (
                            <li key={i} className="text-foreground">{tom}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </TabsContent>
          </Tabs>

          {/* Footer Info */}
          <Card className="mt-8 p-6 bg-secondary/20">
            <p className="text-sm text-muted-foreground">
              <strong>Letzte Aktualisierung:</strong> {new Date().toLocaleDateString('de-AT', { 
                day: '2-digit', 
                month: '2-digit', 
                year: 'numeric' 
              })}
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Dieses Verarbeitungsverzeichnis wurde gemäß Art. 30 DSGVO erstellt und muss regelmäßig 
              aktualisiert werden. Bei Änderungen der Datenverarbeitungstätigkeiten ist dieses Dokument 
              unverzüglich anzupassen.
            </p>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AdminVerarbeitungsverzeichnis;
